import { getTokenMutation } from './graphql/getTokenMutation'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import type { CustomerToken } from '@pylot-data/fwrdschema'
import { mergeCarts, refreshCart } from '@corratech/pylot-cart-manager'
import { GraphQLError } from '@pylot-data/graphqlFetch'
import { Cart } from '@pylot-data/pylotschema'
import { CartData } from '@lib/casaideas-components/pylot-cart-manager'
import { useAccount } from './use-account'
import { CUSTOMER_QUERY } from './graphql/customerQuery'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useAuthUI } from './AuthContext'
import { loginEvent } from '@lib/utils/exponeaTrackingEvents'
import { mutate as globalMutate } from 'swr'

export type GenerateTokenResponse = {
    data: CustomerToken
}

export type LoginArguments = {
    email: string
    password: string
    skipReload?: boolean
}

type AuthLoginProps = {
    updateCart: (
        data: {
            data: {
                cart: Cart
                isOptimistic?: boolean
            }
            errors?: GraphQLError[]
        },
        isOptimistic: boolean
    ) => Promise<any>
    cartData?: CartData
}

type AuthLoginReturnType = {
    login: (params: LoginArguments) => Promise<any>
    isLoading: boolean
}

export const useAuthLogin = ({
    cartData,
    updateCart
}: AuthLoginProps): AuthLoginReturnType => {
    const { mutate: fetchAccountInfo } = useAccount()
    const router = useRouter()
    const { locale } = router
    const { loginCallbacks, clearAuthCallback } = useAuthUI()
    const [isLoading, setIsLoading] = useState(false)

    return {
        login: async (params: LoginArguments) => {
            const { skipReload, ...loginParams } = params
            setIsLoading(true)
            const res = await graphqlFetch<
                Omit<LoginArguments, 'skipReload'>,
                { generateCustomerToken: CustomerToken }
            >({
                query: getTokenMutation,
                variables: loginParams,
                locale
            })
            if (res.errors) {
                setIsLoading(false)
                throw res.errors
            }
            const guestCartId = localStorage.getItem('cart_id') || ''
            localStorage.setItem(
                'pylot_token',
                res.data!.generateCustomerToken.token!
            )
            loginEvent({ email: params.email })
            window.dispatchEvent(new CustomEvent('pylot:refresh-content'))
            const result = await Promise.all([
                globalMutate([CUSTOMER_QUERY]),
                fetchAccountInfo(),
                // Sin carro de invitado (no agregó productos antes de iniciar
                // sesión) no hay nada que fusionar: se trae el carro del cliente.
                guestCartId
                    ? mergeCarts({ guestCartId, locale }).then((res) => {
                          const { errors } = res
                          if (!errors) {
                              updateCart(res, false)
                          } else {
                              updateCart(
                                  {
                                      ...cartData,
                                      data: {
                                          cart: {
                                              ...cartData!.data.cart,
                                              items: [
                                                  ...(cartData!.data.cart.items?.filter(
                                                      (item) => !!item
                                                  ) || [])
                                              ]
                                          }
                                      }
                                  },
                                  false
                              )
                          }
                      })
                    : refreshCart()
            ])
            setIsLoading(false)
            //handle login callbacks
            if (loginCallbacks.length) {
                loginCallbacks.forEach((callback) => {
                    if (typeof callback === 'function') {
                        callback()
                    }
                })
                clearAuthCallback('LOGIN_VIEW')
            } else if (!skipReload) {
                window.dispatchEvent(new CustomEvent('pylot:refresh-content'))
            }
            return result
        },
        isLoading
    }
}
